import React from 'react';
import { Heart, Shield, Sparkles, Eye, Info } from 'lucide-react';
interface EmotionalBondProps {
  angelName?: string;
  stats: {
    trust: number;
    attraction: number;
    intimacy: number;
    mystery: number;
  };
  level: number;
  daysConnected: number;
}
export const EmotionalBondMeter: React.FC<EmotionalBondProps> = ({
  angelName = 'Sophia',
  stats = {
    trust: 72,
    attraction: 85,
    intimacy: 63,
    mystery: 91
  },
  level = 4,
  daysConnected = 28
}) => {
  return <div className="w-full bg-white dark:bg-gray-900 rounded-xl border border-gray-200 dark:border-gray-800 overflow-hidden" data-id="element-6903">
      <div className="p-4 border-b border-gray-200 dark:border-gray-800" data-id="element-6904">
        <h2 className="text-lg font-bold flex items-center gap-2 text-gray-800 dark:text-white" data-id="element-6905">
          <Heart size={20} className="text-pink-400" data-id="element-6906" />
          <span data-id="element-6907">Emotional Bond with {angelName}</span>
        </h2>
        <p className="text-sm text-gray-500 dark:text-gray-400 mt-1" data-id="element-6908">
          Your relationship evolves based on your interactions
        </p>
      </div>
      <div className="p-4" data-id="element-6909">
        <div className="flex items-center justify-between mb-6" data-id="element-6910">
          <div className="flex items-center gap-3" data-id="element-6911">
            <div className="relative" data-id="element-6912">
              <div className="w-16 h-16 rounded-full flex items-center justify-center bg-gradient-to-br from-pink-600 to-purple-600 text-white" data-id="element-6913">
                <span className="text-xl font-bold" data-id="element-6914">{level}</span>
              </div>
              <svg className="absolute inset-0 w-16 h-16 -rotate-90" data-id="element-6915">
                <circle cx="32" cy="32" r="30" fill="none" stroke="#E5E7EB" strokeWidth="4" className="dark:stroke-gray-700" data-id="element-6916" />
                <circle cx="32" cy="32" r="30" fill="none" stroke="url(#bondGradient)" strokeWidth="4" strokeDasharray="188.5" strokeDashoffset={188.5 * (100 - level / 10 * 100) / 100} data-id="element-6917" />
                <defs data-id="element-6918">
                  <linearGradient id="bondGradient" x1="0%" y1="0%" x2="100%" y2="0%" data-id="element-6919">
                    <stop offset="0%" stopColor="#EC4899" data-id="element-6920" />
                    <stop offset="100%" stopColor="#8B5CF6" data-id="element-6921" />
                  </linearGradient>
                </defs>
              </svg>
            </div>
            <div data-id="element-6922">
              <h3 className="font-bold text-gray-800 dark:text-white" data-id="element-6923">
                Connection Level {level}
              </h3>
              <p className="text-sm text-gray-600 dark:text-gray-400" data-id="element-6924">
                {level < 3 ? 'Getting to know each other' : level < 5 ? 'Becoming closer' : level < 7 ? 'Deep connection' : 'Soulmates'}
              </p>
              <p className="text-xs text-gray-500 dark:text-gray-500 mt-1" data-id="element-6925">
                {daysConnected} days together
              </p>
            </div>
          </div>
          <div className="text-right" data-id="element-6926">
            <div className="text-sm font-medium text-gray-700 dark:text-gray-300" data-id="element-6927">
              Next level in:
            </div>
            <div className="text-xl font-bold text-pink-400" data-id="element-6928">147 XP</div>
          </div>
        </div>
        <div className="space-y-4" data-id="element-6929">
          <div data-id="element-6930">
            <div className="flex items-center justify-between mb-1" data-id="element-6931">
              <div className="flex items-center gap-2" data-id="element-6932">
                <Shield size={16} className="text-blue-400" data-id="element-6933" />
                <span className="text-sm font-medium text-gray-700 dark:text-white" data-id="element-6934">
                  Trust
                </span>
              </div>
              <span className="text-sm font-medium text-gray-700 dark:text-white" data-id="element-6935">
                {stats.trust}%
              </span>
            </div>
            <div className="h-2 bg-gray-100 dark:bg-gray-800 rounded-full overflow-hidden" data-id="element-6936">
              <div className="h-full bg-gradient-to-r from-blue-500 to-blue-400 rounded-full transition-all duration-1000 ease-out" style={{
              width: `${stats.trust}%`
            }} data-id="element-6937"></div>
            </div>
            <p className="text-xs text-gray-500 dark:text-gray-500 mt-1" data-id="element-6938">
              {stats.trust < 30 ? 'Building foundations' : stats.trust < 60 ? 'Growing steadily' : stats.trust < 80 ? 'Strong bond' : 'Unbreakable trust'}
            </p>
          </div>
          <div data-id="element-6939">
            <div className="flex items-center justify-between mb-1" data-id="element-6940">
              <div className="flex items-center gap-2" data-id="element-6941">
                <Heart size={16} className="text-pink-400" data-id="element-6942" />
                <span className="text-sm font-medium text-gray-700 dark:text-white" data-id="element-6943">
                  Attraction
                </span>
              </div>
              <span className="text-sm font-medium text-gray-700 dark:text-white" data-id="element-6944">
                {stats.attraction}%
              </span>
            </div>
            <div className="h-2 bg-gray-100 dark:bg-gray-800 rounded-full overflow-hidden" data-id="element-6945">
              <div className="h-full bg-gradient-to-r from-pink-500 to-pink-400 rounded-full transition-all duration-1000 ease-out" style={{
              width: `${stats.attraction}%`
            }} data-id="element-6946"></div>
            </div>
            <p className="text-xs text-gray-500 dark:text-gray-500 mt-1" data-id="element-6947">
              {stats.attraction < 30 ? 'Spark of interest' : stats.attraction < 60 ? 'Magnetic pull' : stats.attraction < 80 ? 'Deeply attracted' : 'Irresistible desire'}
            </p>
          </div>
          <div data-id="element-6948">
            <div className="flex items-center justify-between mb-1" data-id="element-6949">
              <div className="flex items-center gap-2" data-id="element-6950">
                <Sparkles size={16} className="text-purple-400" data-id="element-6951" />
                <span className="text-sm font-medium text-gray-700 dark:text-white" data-id="element-6952">
                  Intimacy
                </span>
              </div>
              <span className="text-sm font-medium text-gray-700 dark:text-white" data-id="element-6953">
                {stats.intimacy}%
              </span>
            </div>
            <div className="h-2 bg-gray-100 dark:bg-gray-800 rounded-full overflow-hidden" data-id="element-6954">
              <div className="h-full bg-gradient-to-r from-purple-500 to-purple-400 rounded-full transition-all duration-1000 ease-out" style={{
              width: `${stats.intimacy}%`
            }} data-id="element-6955"></div>
            </div>
            <p className="text-xs text-gray-500 dark:text-gray-500 mt-1" data-id="element-6956">
              {stats.intimacy < 30 ? 'Getting comfortable' : stats.intimacy < 60 ? 'Opening up' : stats.intimacy < 80 ? 'Deep connection' : 'Profound intimacy'}
            </p>
          </div>
          <div data-id="element-6957">
            <div className="flex items-center justify-between mb-1" data-id="element-6958">
              <div className="flex items-center gap-2" data-id="element-6959">
                <Eye size={16} className="text-emerald-400" data-id="element-6960" />
                <span className="text-sm font-medium text-gray-700 dark:text-white" data-id="element-6961">
                  Mystery
                </span>
              </div>
              <span className="text-sm font-medium text-gray-700 dark:text-white" data-id="element-6962">
                {stats.mystery}%
              </span>
            </div>
            <div className="h-2 bg-gray-100 dark:bg-gray-800 rounded-full overflow-hidden" data-id="element-6963">
              <div className="h-full bg-gradient-to-r from-emerald-500 to-emerald-400 rounded-full transition-all duration-1000 ease-out" style={{
              width: `${stats.mystery}%`
            }} data-id="element-6964"></div>
            </div>
            <p className="text-xs text-gray-500 dark:text-gray-500 mt-1" data-id="element-6965">
              {stats.mystery < 30 ? 'Predictable' : stats.mystery < 60 ? 'Intriguing' : stats.mystery < 80 ? 'Mysterious' : 'Endlessly fascinating'}
            </p>
          </div>
        </div>
        <div className="mt-6 p-3 bg-gray-100 dark:bg-gray-800/50 rounded-lg border border-gray-200 dark:border-gray-700/50 flex items-start gap-3" data-id="element-6966">
          <Info size={18} className="text-blue-400 flex-shrink-0 mt-0.5" data-id="element-6967" />
          <div data-id="element-6968">
            <p className="text-sm text-gray-600 dark:text-gray-300" data-id="element-6969">
              Your emotional bond grows through meaningful interactions. Share
              your thoughts, respond to questions, and engage in deeper
              conversations to strengthen your connection.
            </p>
            <button className="text-xs text-pink-400 mt-2" data-id="element-6970">
              Learn how to deepen your connection
            </button>
          </div>
        </div>
      </div>
      <div className="p-4 border-t border-gray-200 dark:border-gray-800 bg-gray-50 dark:bg-gray-900/80" data-id="element-6971">
        <div className="flex items-center justify-between" data-id="element-6972">
          <div className="text-sm text-gray-500 dark:text-gray-400" data-id="element-6973">
            Last updated: 2 hours ago
          </div>
          <button className="px-4 py-2 bg-gradient-to-r from-pink-600 to-purple-600 rounded-full text-white text-sm font-medium" data-id="element-6974">
            Strengthen Bond
          </button>
        </div>
      </div>
    </div>;
};